import { Injectable } from '@angular/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { PhmDashboardReq } from '../../model/inko-backend.model'
import { DashboardZeitraeume, mitAktuellenZeitraeumen } from '../../util/dashboard-filter'

/** Der Filter samt der Marken mitlaufender Zeitraeume (`DashboardZeitraeume`). */
export type PhmDashboardFilter = PhmDashboardReq & DashboardZeitraeume

/* Model */

export interface PhmDashboardStateModel {
  version: number

  phmColumns: {
    /**
     * All available column keys (in order)
     */
    keys: string[]
    /**
     * Currently visible column keys
     */
    visible: string[]
  }
  phmFilter: PhmDashboardFilter
  /**
   * Schluessel der fest eingebauten Kacheln, die der Nutzer entfernt hat. Bewusst eine Liste der
   * ENTFERNTEN statt der sichtbaren: so erreichen spaeter ergaenzte Preset-Kacheln auch Nutzer,
   * die ihre Kacheln schon einmal angepasst haben.
   */
  phmDismissedPresets?: string[]
  phmKpiCards: {
    title: string
    phmFilter: PhmDashboardFilter
  }[]
}

// Phm

export const phmAvailableColumns: { label: string; key: string; defaultVisible?: boolean }[] = [
  { label: 'Produktgruppe', key: 'produktgruppe', defaultVisible: true },
  { label: 'Netto Betrag', key: 'nettoBetrag' },
  { label: 'Datum', key: 'createdDate', defaultVisible: true },
  { label: 'Patientenname', key: 'patientenname', defaultVisible: true },
  { label: 'Geburtsdatum', key: 'dateOfBirth' },
  { label: 'Krankenkasse', key: 'krankenkasse', defaultVisible: true },
  { label: 'Versicherungsart', key: 'versicherungsart', defaultVisible: false },
  { label: 'Zuzahlungsbefreiung', key: 'zuzahlungsbefreiung', defaultVisible: false },
  { label: 'Herkunft', key: 'externerPartner', defaultVisible: true },
  { label: 'Genehmigungsart', key: 'genehmigungsart' },
  { label: 'Anlagestatus', key: 'anlagestatus', defaultVisible: true },
  { label: 'Genehmigungsstatus', key: 'genehmigungsstatus', defaultVisible: true },
  { label: 'Versorgungsstatus', key: 'versorgungsstatus', defaultVisible: true },
  { label: 'Lieferungsstatus', key: 'lieferungsstatus', defaultVisible: true },
]
export const phmColumnNameMap = phmAvailableColumns.reduce(
  (acc, col) => {
    acc[col.key] = col.label
    return acc
  },
  {} as Record<string, string>,
)

/* Actions */

/**
 * Update the PHM columns from the available columns
 */
export class DashboardStatePhmUpdateColumns {
  static readonly type = '[Dashboard] Update PHM Columns'
}

export class DashboardStatePhmMoveColumns {
  static readonly type = '[Dashboard] Move PHM Columns'
  constructor(
    public indexA: number,
    public indexB: number,
  ) {}
}

export class DashboardStatePhmSetColumnVisible {
  static readonly type = '[Dashboard] Visible PHM Column'
  constructor(public keys: string[]) {}
}

export class DashboardStatePhmReset {
  static readonly type = '[Dashboard] Reset PHM Columns'
}

export class DashboardStatePhmSetFilter {
  static readonly type = '[Dashboard] Set PHM Filter'
  constructor(public filter: Partial<PhmDashboardFilter>) {}
}
export class DashboardStatePhmResetFilter {
  static readonly type = '[Dashboard] Reset PHM Filter'
}

export class DashboardStatePhmAddKpiCard {
  static readonly type = '[Dashboard] Add PHM KPI Card'
  constructor(
    public title: string,
    public filter: PhmDashboardFilter,
  ) {}
}
export class DashboardStatePhmRemoveKpiCard {
  static readonly type = '[Dashboard] Remove PHM KPI Card'
  constructor(public index: number) {}
}

export class DashboardStatePhmDismissPresetKpiCard {
  static readonly type = '[Dashboard] Dismiss Phm Preset KPI Card'
  constructor(public key: string) {}
}
export class DashboardStatePhmRestorePresetKpiCards {
  static readonly type = '[Dashboard] Restore Phm Preset KPI Cards'
}

/* State */

const defaultState: PhmDashboardStateModel = {
  version: 1,

  phmColumns: {
    keys: phmAvailableColumns.map((c) => c.key),
    visible: phmAvailableColumns.filter((c) => c.defaultVisible).map((c) => c.key),
  },
  phmFilter: {
    pageSize: 30,
  },
  phmDismissedPresets: [],
  phmKpiCards: [],
}

@State<PhmDashboardStateModel>({
  name: 'phmDashboard',
  defaults: defaultState,
})
@Injectable()
export class PhmDashboardState {
  @Selector()
  static phmAvailableColumns(state: PhmDashboardStateModel) {
    return state.phmColumns.keys
  }
  @Selector()
  static phmVisibleColumns(state: PhmDashboardStateModel) {
    return state.phmColumns.visible
  }
  @Selector()
  static phmFilter(state: PhmDashboardStateModel) {
    // Mitlaufende Zeitraeume („Letzter Monat“) auf heute ausrechnen — der persistierte Stand kann von gestern sein.
    return mitAktuellenZeitraeumen(state.phmFilter)
  }

  @Selector()
  static phmKpiCards(state: PhmDashboardStateModel) {
    return state.phmKpiCards
  }

  @Selector()
  static phmDismissedPresets(state: PhmDashboardStateModel): string[] {
    // Persistierter Altstand kennt das Feld nicht — ohne Fallback waere es undefined.
    return state.phmDismissedPresets ?? []
  }

  @Selector()
  static phmFilterCounter(state: PhmDashboardStateModel) {
    const filter = state.phmFilter
    return (
      (filter.healthInsuranceIds?.length ?? 0) +
      (filter.externalPartnerIds?.length ?? 0) +
      (filter.pflegeboxVersorgungType ? 1 : 0) +
      (filter.openTaskForAssignee ? 1 : 0) +
      (filter.withOpenPg51NeuBeantragenTask ? 1 : 0) +
      (filter.dashboardEkvRequestMethod ? 1 : 0) +
      (filter.insuranceType ? 1 : 0) +
      (filter.genehmigungStatus?.length ?? 0) +
      (filter.anlageStatus?.length ?? 0) +
      (filter.versorgungsStatus?.length ?? 0) +
      (filter.ekvRequestFromDate || filter.ekvRequestUntilDate ? 1 : 0) +
      (filter.fromCreatedDate || filter.toCreatedDate ? 1 : 0)
    )
  }

  // Phm

  @Action(DashboardStatePhmUpdateColumns)
  setPhmColumns(ctx: StateContext<PhmDashboardStateModel>) {
    const { version } = ctx.getState()
    if (version != defaultState.version) {
      ctx.setState({
        ...defaultState,
        phmKpiCards: ctx.getState().phmKpiCards ?? defaultState.phmKpiCards,
      })
      return
    }

    const columns = ctx.getState().phmColumns.keys.slice()
    const allColumnKeys = phmAvailableColumns.map((c) => c.key)

    // Add any new columns that are not already in the state
    allColumnKeys.forEach((key) => {
      if (!columns.includes(key)) {
        columns.push(key)
      }
    })
    // remove any columns that are no longer available
    const filteredColumns = columns.filter((key) => allColumnKeys.includes(key))

    ctx.patchState({
      phmColumns: {
        ...ctx.getState().phmColumns,
        keys: filteredColumns,
      },
    })
  }

  @Action(DashboardStatePhmMoveColumns)
  movePhmColumns(ctx: StateContext<PhmDashboardStateModel>, action: DashboardStatePhmMoveColumns) {
    const columns = ctx.getState().phmColumns.keys.slice()
    const [movedColumn] = columns.splice(action.indexA, 1)
    columns.splice(action.indexB, 0, movedColumn)
    ctx.patchState({
      phmColumns: {
        ...ctx.getState().phmColumns,
        keys: columns,
      },
    })
  }

  @Action(DashboardStatePhmSetColumnVisible)
  setPhmColumnVisible(ctx: StateContext<PhmDashboardStateModel>, action: DashboardStatePhmSetColumnVisible) {
    ctx.patchState({
      phmColumns: {
        ...ctx.getState().phmColumns,
        visible: action.keys,
      },
    })
  }

  @Action(DashboardStatePhmReset)
  resetPhmColumns(ctx: StateContext<PhmDashboardStateModel>) {
    ctx.patchState({
      phmColumns: defaultState.phmColumns,
    })
  }

  @Action(DashboardStatePhmSetFilter)
  setPhmFilter(ctx: StateContext<PhmDashboardStateModel>, action: DashboardStatePhmSetFilter) {
    ctx.patchState({
      phmFilter: {
        ...ctx.getState().phmFilter,
        ...action.filter,
      },
    })
  }

  @Action(DashboardStatePhmResetFilter)
  resetPhmFilter(ctx: StateContext<PhmDashboardStateModel>) {
    ctx.patchState({
      phmFilter: defaultState.phmFilter,
    })
  }

  @Action(DashboardStatePhmAddKpiCard)
  addPhmKpiCard(ctx: StateContext<PhmDashboardStateModel>, action: DashboardStatePhmAddKpiCard) {
    ctx.patchState({
      phmKpiCards: [
        ...ctx.getState().phmKpiCards,
        {
          title: action.title,
          phmFilter: action.filter,
        },
      ],
    })
  }
  @Action(DashboardStatePhmRemoveKpiCard)
  removePhmKpiCard(ctx: StateContext<PhmDashboardStateModel>, action: DashboardStatePhmRemoveKpiCard) {
    const currentCards = ctx.getState().phmKpiCards.slice()
    currentCards.splice(action.index, 1)
    ctx.patchState({
      phmKpiCards: currentCards,
    })
  }
  @Action(DashboardStatePhmDismissPresetKpiCard)
  dismissPhmPresetKpiCard(ctx: StateContext<PhmDashboardStateModel>, action: DashboardStatePhmDismissPresetKpiCard) {
    const dismissed = ctx.getState().phmDismissedPresets ?? []
    if (dismissed.includes(action.key)) {
      return
    }
    ctx.patchState({ phmDismissedPresets: [...dismissed, action.key] })
  }

  @Action(DashboardStatePhmRestorePresetKpiCards)
  restorePhmPresetKpiCards(ctx: StateContext<PhmDashboardStateModel>) {
    // Nur die Presets kommen zurueck; selbst angelegte Kacheln bleiben unangetastet.
    ctx.patchState({ phmDismissedPresets: [] })
  }
}
